/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Button, Dialog, breakpoints } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/coursera-plus';

type PropsToComponent = {
  isOpen: boolean;
  onClose: () => void;
  subscriptionCount: number;
};

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const useStyles = () => {
  return {
    topLogo: css`
      margin-bottom: 24px;
      padding-left: 48px;
      ${breakpoints.down('sm')} {
        margin-top: 2px;
      }
      ${breakpoints.down('xs')} {
        padding-left: 16px;
      }
    `,
    actions: css`
      padding-top: 16px;
    `,
  };
};

const S12nToCourseraPlusIneligibleModal = ({ isOpen, onClose, subscriptionCount }: PropsToComponent) => {
  const { Content, HeadingGroup, Actions } = Dialog;
  const styles = useStyles();

  const handleCloseModal = () => {
    onClose();
  };

  const getCopy = () => {
    if (subscriptionCount === 0) {
      return {
        title: _t("We're sorry, this offer is no longer available."),
        description: _t(
          "This upgrade was linked to an active subscription, which has since been canceled. But don't worry - you can resubscribe at any time to continue with full access."
        ),
      };
    } else {
      return {
        title: _t(`We're sorry this upgrade isn't available yet`),
        description: _t(
          `We're sorry for the inconvenience. This upgrade isn't yet available for learners who already have 2 or more active subscriptions on Coursera. Check back soon for updates - thanks for your patience.`
        ),
      };
    }
  };

  const copy = getCopy();

  return (
    <Dialog
      aria-describedby="dialog-content"
      open={isOpen}
      variant="standard"
      width="medium"
      onClose={handleCloseModal}
      data-testid="s12n_to_coursera_plus_start_modal"
    >
      <Imgix src={logo.PLUS_FULL_BLUE} alt={_t('Coursera Plus logo')} maxWidth={200} css={styles.topLogo} />
      <HeadingGroup>{copy.title}</HeadingGroup>
      <Content>
        <TrackedDiv withVisibilityTracking={true} trackingName="s12n_to_coursera_plus_ineligible_modal">
          {copy.description}
        </TrackedDiv>
      </Content>
      <Actions>
        <TrackedButton
          variant="primary"
          onClick={onClose}
          icon={<ArrowNextIcon size="medium" />}
          data-testid="s12n-to-coursera-plus-ineligible-dismiss-button"
          trackingName="s12n_to_coursera_plus_ineligible_dismiss_button"
        >
          {_t('Dismiss')}
        </TrackedButton>
      </Actions>
    </Dialog>
  );
};

export default S12nToCourseraPlusIneligibleModal;
